/* SIZESOFTX
=========================================================================================
* Descripción: Catálogo de Corridas (tallas)
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1        | 25/04/2026 | Lic. José Luis Aragón Cervantes  | Creación
* ---------------------------------------------------------------------------------------
*/

import TextField from "../../ui/TextField";
import { Fragment, useEffect, useState } from "react";
import { Box, Card, CardContent, Divider, Grid } from "@mui/material";

import SizeSoftXLayout from "../../../components/leyout/SizeSoftXLayout.jsx";
import SizeSoftXTableCatalogos from "../../../components/leyout/SizeSoftXTableCatalogos.jsx";
import MnuCorridasActions from "./MnuCorridasActions.jsx";
import SizeSoftXModal from "../../../components/leyout/SizeSoftXModal.jsx";
import { useCorridas } from "../../../context/Catalogos/CorridasContext";
import { useLayout } from "../../../context/System/LayoutContext";
import { useFormTabLock } from "../../../hooks/useFormTabLock";

export default function FrmCorridas() {
    const { isLocked: isFormLocked } = useFormTabLock('corridas', {
        title: 'Corridas',
        onLockDetected: (info) => {
            console.warn(`El formulario de ${info?.title || 'Corridas'} ya está abierto en otra pestaña`);
        }
    });

    const {
        corridaSeleccionada,
        setCorridaSeleccionada,
        corridaPayLoad,
        setCorridaPayLoad,
        listaCorridas,
        statusEnableFields,
        setStatusEnableFields,
        statusEditCorridas,
        setStatusEditCorridas,
        statusOpenModalEliminarCorrida,
        setStatusOpenModalEliminarCorrida,
        handleClickNuevaCorrida,
        handleClickGuardarCorrida,
        handleClickEliminarCorrida,
        handleClickRegresarMainDashboard,
        handleClickRefrescarDatos,
        getStructCorridasPayload,
        handleChangeDataCorridasPayload,
        handleObtenerCorridasList,
        handleClickEliminarTodasCorridas,
    } = useCorridas();

    const [statusOpenModalEliminarTodas, setStatusOpenModalEliminarTodas] = useState(false);

    const { handleChangeTitle, handleChangeClavePaginaActual, handleOpenAlert } = useLayout();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [listaCorridas]);

    useEffect(() => {
        if (isFormLocked) {
            handleOpenAlert("El formulario de Corridas ya está abierto en otra pestaña del navegador.\n\nPor favor, cierre la otra ventana o pestaña para continuar.", "error");
            setTimeout(() => {
                if(handleClickRegresarMainDashboard) handleClickRegresarMainDashboard();
            }, 900);
        }
    }, [isFormLocked, handleClickRegresarMainDashboard, handleOpenAlert]);

    const esModoNuevo = !corridaSeleccionada?.descripcion || corridaSeleccionada.descripcion.trim() === "";

    const handleClickRow = (corrida) => {
        // Siempre cargar datos y habilitar edición
        setCorridaSeleccionada(corrida);
        
        const payload = getStructCorridasPayload();
        Object.keys(payload).forEach((key) => {
            if (corrida[key] !== undefined) {
                if (key === "medidainicial" || key === "medidafinal" || key === "intervalos") {
                    payload[key] = { value: parseFloat(corrida[key]) || 0, error: false, helper: "" };
                } else {
                    payload[key] = { value: corrida[key] ?? "", error: false, helper: "" };
                }
            }
        });
        setCorridaPayLoad(payload);
        
        // Habilitar edición siempre
        setStatusEditCorridas(false);
        setStatusEnableFields(false);
    };

    const columns = [
        { title: "Descripción", field: "descripcion", width: 180 },
        { title: "Medida Inicial", field: "medidainicial", width: 100 },
        { title: "Medida Final", field: "medidafinal", width: 100 },
        { title: "Intervalos", field: "intervalos", width: 80 },
        { title: "Fecha Registro", field: "fecharegistro", width: 140 },
    ];

    useEffect(() => {
        handleObtenerCorridasList();
        handleChangeTitle("Catálogo de Corridas");
        handleChangeClavePaginaActual("Corridas");
    }, []);

    return (
        <Fragment>
            <SizeSoftXLayout
                titlePage="Catálogo de Corridas"
                buttonActions={<MnuCorridasActions onOpenModalEliminarTodas={() => setStatusOpenModalEliminarTodas(true)} />}
            >
                <Box>
                    <Box sx={{ mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 2, bgcolor: '#f9f9f9' }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={5}>
                                <TextField autoComplete="off"
                                    label="Descripción (Clave)"
                                    name="descripcion"
                                    value={corridaPayLoad.descripcion.value ?? ""}
                                    onChange={handleChangeDataCorridasPayload}
                                    disabled={statusEnableFields || !esModoNuevo}
                                    fullWidth
                                    size="small"
                                    inputProps={{ maxLength: 30 }}
                                    error={corridaPayLoad?.descripcion?.error}
                                    helperText={corridaPayLoad?.descripcion?.helper}
                                />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <TextField autoComplete="off"
                                    label="Medida Inicial"
                                    name="medidainicial"
                                    type="text"
                                    inputMode="numeric"
                                    enterKeyHint="enter"
                                    value={corridaPayLoad.medidainicial.value ?? ""}
                                    onChange={handleChangeDataCorridasPayload}
                                    disabled={statusEnableFields}
                                    fullWidth
                                    size="small"
                                    inputProps={{ step: 0.1, min: 0, max: 9999 }}
                                    error={corridaPayLoad?.medidainicial?.error}
                                    helperText={corridaPayLoad?.medidainicial?.helper}
                                />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <TextField autoComplete="off"
                                    label="Medida Final"
                                    name="medidafinal"
                                    type="text"
                                    inputMode="numeric"
                                    enterKeyHint="enter"
                                    value={corridaPayLoad.medidafinal.value ?? ""}
                                    onChange={handleChangeDataCorridasPayload}
                                    disabled={statusEnableFields}
                                    fullWidth
                                    size="small"
                                    inputProps={{ step: 0.1, min: 0, max: 9999 }}
                                    error={corridaPayLoad?.medidafinal?.error}
                                    helperText={corridaPayLoad?.medidafinal?.helper}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <TextField autoComplete="off"
                                    label="Intervalos"
                                    name="intervalos"
                                    type="text"
                                    inputMode="numeric"
                                    enterKeyHint="enter"
                                    value={corridaPayLoad.intervalos.value ?? ""}
                                    onChange={handleChangeDataCorridasPayload}
                                    disabled={statusEnableFields}
                                    fullWidth
                                    size="small"
                                    inputProps={{ step: 0.1, min: 0.1, max: 9999 }}
                                    error={corridaPayLoad?.intervalos?.error}
                                    helperText={corridaPayLoad?.intervalos?.helper}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                    
                    <Card>
                        <CardContent sx={{ p: 1 }}>
                            <SizeSoftXTableCatalogos
                                columns={columns}
                                data={listaCorridas}
                                id="descripcion"
                                row={corridaSeleccionada}
                                setRow={handleClickRow}
                            />
                        </CardContent>
                    </Card>
                </Box>
            </SizeSoftXLayout>

            <SizeSoftXModal
                open={statusOpenModalEliminarCorrida}
                title={"¿Está seguro de querer eliminar la Corrida? Esta acción no se puede deshacer."}
                onClose={() => setStatusOpenModalEliminarCorrida(false)}
                onSuccess={handleClickEliminarCorrida}
                success="Eliminar"
            />

            <SizeSoftXModal
                open={statusOpenModalEliminarTodas}
                title={"¿Está seguro de querer eliminar TODAS las Corridas? Esta acción eliminará todos los registros y no se puede deshacer."}
                onClose={() => setStatusOpenModalEliminarTodas(false)}
                onSuccess={async () => {
                    await handleClickEliminarTodasCorridas();
                    setStatusOpenModalEliminarTodas(false);
                }}
                success="Eliminar Todo"
            />
        </Fragment>
    );
}